import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import RichTextEditor from "@/components/ui/rich-text-editor";
import AttachmentUpload from "@/components/emails/AttachmentUpload";
import SignatureModal from "@/components/emails/SignatureModal";
import WarningManager, { WarningManagerRef } from "@/components/emails/WarningManager";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Player } from "@/lib/types";

interface EmailTemplate {
  id: number;
  name: string;
  subject: string;
  content: string;
  category?: string;
  tags?: string[];
  user_ids?: number;
  usage_count?: number;
  created_at?: string;
  updated_at?: string;
}

interface EmailVariables {
  PlayerName: string;
  PlayerFirstName: string;
  PlayerLastName: string;
  PlayerPosition: string;
  PlayerClub: string;
  PlayerLevel: string;
}

interface SendEmailData {
  to: string | string[];
  templateId?: number | string;
  subject: string;
  content: string;
  scheduledAt?: string;
  trackOpens: boolean;
  trackClicks: boolean;
  recipientId?: number;
  variables?: EmailVariables;
  selectedPlayerData?: Player | null;
  emailTags?: string[];
  selectedPlayerIds?: number[];
  attachments?: AttachmentFile[];
}

interface AttachmentFile {
  id: string;
  file?: File;
  name: string;
  size: number;
  type: string;
  progress: number;
  status: 'uploading' | 'completed' | 'error';
  error?: string;
  url?: string;
  serverId?: string;
  attachment_id?: number;
  file_name?: string;
  file_size?: number;
  file_type?: string;
  upload_date?: string;
  storage_path?: string;
  isExisting?: boolean;
}

interface Signature {
  name: string;
  title: string;
  organization: string;
  department: string;
  email: string;
  phone: string;
  other: string[];
}

interface MissingVariableIssue {
  playerName: string;
  playerId: number;
  missingVariables: { variable: keyof EmailVariables; label: string; }[];
}

interface BulkEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  players: Player[];
  title: string;
  subtitle?: string;
}

// Function to replace variables with actual values
const replaceEmailVariables = (content: string, player: Player | null, variables?: EmailVariables): string => {
  if (!player && !variables) return content;

  const { firstName, lastName } = splitPlayerName(player?.name || '');
  
  const dynamicVariables: EmailVariables = variables || {
    PlayerName: player?.name || 'Player Name',
    PlayerFirstName: firstName || 'First Name',
    PlayerLastName: lastName || 'Last Name',
    PlayerPosition: (player?.position && Array.isArray(player.position) && player.position.length > 0) ? player.position.join(', ') : 'Position',
    PlayerClub: player?.current_club || 'Current Club',
    PlayerLevel: player?.playing_level || 'Playing Level',
  };

  return content.replace(/\{\{([^}]+)\}\}/g, (match, variableName) => {
    const key = variableName.trim() as keyof EmailVariables;
    return dynamicVariables[key] || match;
  });
};

// Function to split player name
const splitPlayerName = (fullName: string) => {
  if (!fullName || !fullName.trim()) {
    return { firstName: '', lastName: '' };
  }
  
  const nameParts = fullName.trim().split(' ');
  
  if (nameParts.length === 1) {
    return { firstName: nameParts[0], lastName: '' };
  } else if (nameParts.length === 2) {
    return { firstName: nameParts[0], lastName: nameParts[1] };
  } else {
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ');
    return { firstName, lastName };
  }
};

// Function to validate variables for players
const validateVariablesForPlayers = (content: string, subject: string, players: Player[]): MissingVariableIssue[] => {
  const allContent = `${subject} ${content}`;
  const usedVariables = extractVariablesFromContent(allContent);
  
  if (usedVariables.length === 0) {
    return [];
  }
  
  const issues: MissingVariableIssue[] = [];
  
  players.forEach(player => {
    const missingVariables: { variable: keyof EmailVariables; label: string; }[] = [];
    
    usedVariables.forEach(variable => {
      if (!hasVariableValue(player, variable)) {
        missingVariables.push({
          variable,
          label: getVariableLabel(variable)
        });
      }
    });
    
    if (missingVariables.length > 0) {
      issues.push({
        playerName: player.name || 'Unknown Player',
        playerId: player.id,
        missingVariables
      });
    }
  });
  
  return issues;
};

// Function to extract variables used in content
const extractVariablesFromContent = (content: string): (keyof EmailVariables)[] => {
  const variableMatches = content.match(/\{\{([^}]+)\}\}/g) || [];
  const extractedVariables: (keyof EmailVariables)[] = [];
  
  variableMatches.forEach(match => {
    const variableName = match.replace(/[{}]/g, '').trim() as keyof EmailVariables;
    if (['PlayerName', 'PlayerFirstName', 'PlayerLastName', 'PlayerPosition', 'PlayerClub', 'PlayerLevel'].includes(variableName)) {
      if (!extractedVariables.includes(variableName)) {
        extractedVariables.push(variableName);
      }
    }
  });
  
  return extractedVariables;
};

// Function to check if variable has value
const hasVariableValue = (player: Player, variable: keyof EmailVariables): boolean => {
  const { firstName, lastName } = splitPlayerName(player.name || '');
  
  switch (variable) {
    case 'PlayerName':
      return !!(player.name && player.name.trim());
    case 'PlayerFirstName':
      return !!(firstName && firstName.trim());
    case 'PlayerLastName':
      return !!(lastName && lastName.trim());
    case 'PlayerPosition':
      return !!(player.position && Array.isArray(player.position) && player.position.length > 0);
    case 'PlayerClub':
      return !!(player.current_club && player.current_club.trim());
    case 'PlayerLevel':
      return !!(player.playing_level && player.playing_level.trim());
    default:
      return false;
  }
};

// Function to get variable label
const getVariableLabel = (variable: keyof EmailVariables): string => {
  const labels = {
    PlayerName: 'Player Name',
    PlayerFirstName: 'First Name',
    PlayerLastName: 'Last Name',
    PlayerPosition: 'Position',
    PlayerClub: 'Current Club',
    PlayerLevel: 'Playing Level'
  };
  return labels[variable] || variable;
};

export default function BulkEmailModal({ isOpen, onClose, players, title, subtitle }: BulkEmailModalProps) {
  // Filter to only include players with emails
  const playersWithEmails = players.filter(p => p.email && p.email.trim());
  
  // Add ref to track template application
  const isApplyingTemplate = useRef(false);
  
  const [formData, setFormData] = useState<SendEmailData>({
    to: playersWithEmails.map(p => p.email!),
    templateId: "",
    subject: "",
    content: "",
    scheduledAt: "",
    trackOpens: true,
    trackClicks: true,
    selectedPlayerData: playersWithEmails[0] || null,
    emailTags: playersWithEmails.map(p => p.email!),
    selectedPlayerIds: playersWithEmails.map(p => p.id),
    attachments: [],
  });

  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const [signature, setSignature] = useState<Signature>({
    name: "Your Name",
    title: "Your Title",
    organization: "Your University",
    department: "Your Department",
    email: "your.email@university.edu",
    phone: "Your Phone Number",
    other: [],
  });
  const [showEditSignature, setShowEditSignature] = useState(false);
  const [showConfirmSend, setShowConfirmSend] = useState(false);
  
  // WarningManager ref for centralized warning handling
  const warningManagerRef = useRef<WarningManagerRef>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedPlayerForPreview, setSelectedPlayerForPreview] = useState<Player | null>(playersWithEmails[0] || null);
  const [focusedField, setFocusedField] = useState<'subject' | 'content'>('content');

  // New state for custom dialogs
  const [aiPrompt, setAiPrompt] = useState("");

  const { toast } = useToast();

  // Fetch templates
  const { data: templates } = useQuery<EmailTemplate[]>({
    queryKey: ["/api/email-templates/"],
  });

  // Fetch user profile and preferences
  const { data: userProfile } = useQuery({
    queryKey: ["/api/auth/me"],
  });

  const { data: userPreferences } = useQuery({
    queryKey: ["/api/preferences/"],
  });

  // Initialize form with player data
  useEffect(() => {
    if (playersWithEmails.length > 0) {
      const emails = playersWithEmails.map(player => player.email!);
      const ids = playersWithEmails.map(player => player.id);

      setFormData(prev => {
        if (
          prev.to === emails.join(', ') &&
          JSON.stringify(prev.emailTags) === JSON.stringify(emails) &&
          JSON.stringify(prev.selectedPlayerIds) === JSON.stringify(ids)
        ) {
          return prev;
        }
        return {
          ...prev,
          to: emails.join(', '),
          emailTags: emails,
          selectedPlayerIds: ids,
        };
      });

      if (playersWithEmails[0] && !selectedPlayerForPreview) {
        setSelectedPlayerForPreview(playersWithEmails[0]);
      }
    }
    // Only run when modal opens or players change
  }, [isOpen, playersWithEmails]);

  // Reset modal state when it opens (only on initial open)
  useEffect(() => {
    if (isOpen && playersWithEmails.length > 0 && !isApplyingTemplate.current) {
      const emails = playersWithEmails.map(player => player.email!);
      const ids = playersWithEmails.map(player => player.id);
      
      // Reset all form data to initial state
      setFormData({
        to: emails,
        templateId: "",
        subject: "",
        content: "",
        scheduledAt: "",
        trackOpens: true,
        trackClicks: true,
        selectedPlayerData: playersWithEmails[0] || null,
        emailTags: emails,
        selectedPlayerIds: ids,
        attachments: [],
      });
      
      // Reset other states
      setAttachments([]);
      setErrors({});
      setShowConfirmSend(false);
      setFocusedField('content');
      
      // Reset preview player selection only if not already set
      if (!selectedPlayerForPreview) {
        setSelectedPlayerForPreview(playersWithEmails[0] || null);
      }
    }
  }, [isOpen]);

  // Load signature from preferences
  useEffect(() => {
    if (userPreferences && (userPreferences as any)?.email_signature) {
      try {
        const parsedSignature = JSON.parse((userPreferences as any).email_signature);
        setSignature({
          name: parsedSignature.name || "Your Name",
          title: parsedSignature.title || "Your Title",
          organization: parsedSignature.organization || "Your University",
          department: parsedSignature.department || "Your Department",
          email: parsedSignature.email || "your.email@university.edu",
          phone: parsedSignature.phone || "Your Phone Number",
          other: Array.isArray(parsedSignature.other) ? parsedSignature.other : [],
        });
      } catch (error) {
        console.error("Failed to parse email signature:", error);
      }
    } else if (userProfile) {
      setSignature({
        name: (userProfile as any)?.name || "Your Name",
        title: "Your Title",
        organization: (userProfile as any)?.school || "Your University",
        department: "Your Department",
        email: (userProfile as any)?.email || "your.email@university.edu",
        phone: "Your Phone Number",
        other: [],
      });
    }
  }, [userProfile, userPreferences]);

  // Mutations
  const sendEmailMutation = useMutation({
    mutationFn: async (data: SendEmailData) => {
      console.log('=== SENDING BULK EMAIL ===');
      console.log('Email data being sent:', data);
      console.log('Attachments in email data:', data.attachments);
      console.log('Recipients count:', data.emailTags?.length);
      
      const response = await apiRequest("POST", "/api/email-sending/send", data);
      
      console.log('Bulk email send response:', response);
      
      // Handle HTTP errors before parsing JSON
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch {
          // If JSON parsing fails, use status text
          errorMessage = response.statusText || `HTTP Error ${response.status}`;
        }
        throw new Error(errorMessage);
      }
      
      return response.json();
    },
    onSuccess: (response) => {
      console.log('Bulk email send success:', response);
      
      // Validate actual success from backend response
      const overallStatus = response?.overall_status;
      const successfulSends = response?.successful_sends || 0;
      const totalRecipients = response?.total_recipients || 0;
      const failedSends = response?.failed_sends || 0;
      
      // Only show success if emails were actually sent
      if (overallStatus === "sent" && successfulSends > 0) {
        const emailCount = formData.emailTags?.length || playersWithEmails.length;
        const displayText = `${emailCount} recipient${emailCount !== 1 ? 's' : ''}`;
        
        toast({
          title: "✅ Emails sent successfully!",
          description: `Your email was delivered to ${displayText}`,
        });
      } else if (overallStatus === "partial" && successfulSends > 0) {
        // Partial success - some emails sent, some failed
        toast({
          title: "⚠️ Partially sent",
          description: `Email delivered to ${successfulSends} of ${totalRecipients} recipients.`,
          variant: "default",
        });
      } else {
        // No emails were actually sent
        throw new Error("No emails were sent successfully");
      }
      
      // Email sent successfully - no need to reset warning states as they're handled by WarningManager
      
      queryClient.invalidateQueries({ queryKey: ["/api/emails/"] });
      queryClient.invalidateQueries({ queryKey: ["/api/emails/sent-emails"] });
      
      onClose();
    },
    onError: (error: any) => {
      console.error('Bulk email send error:', error);
      
      // More detailed error messages
      let errorMessage = "Something went wrong";
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      // Check if this is a session expiration error
      const isSessionExpired = errorMessage.includes("Session Has Expired") || 
                               errorMessage.includes("Session Expired") ||
                               errorMessage.includes("Please log out and") ||
                               (errorMessage.includes("401") && errorMessage.includes("authentication"));
      
      if (isSessionExpired) {
        toast({
          title: "❌ Session Expired",
          description: "Your Session Has Expired. Please log out and log in again.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "❌ Failed to send emails",
          description: errorMessage,
          variant: "destructive",
        });
      }
    },
  });

  // Warning check functions for WarningManager
  const checkGraduationWarnings = async (emailTags: string[]) => {
    const response = await apiRequest("POST", "/api/email-sending/check-graduation-warnings", {
      emailTags
    });
    return response.json();
  };

  const checkDuplicateEmails = async (emailTags: string[]) => {
    const response = await apiRequest("POST", "/api/email-sending/check-duplicate-emails", {
      emailTags
    });
    return response.json();
  };

  // Handler functions
  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleTemplateSelect = async (templateId: string) => {
    isApplyingTemplate.current = true; // Prevent reset during template application
    
    const template = templates?.find((t) => t.id === parseInt(templateId));
    if (template) {
      setFormData(prev => ({
        ...prev,
        templateId,
        subject: template.subject,
        content: template.content,
        // Preserve recipient data - don't overwrite these fields
        // to: prev.to,
        // emailTags: prev.emailTags,
        // selectedPlayerData: prev.selectedPlayerData,
        // selectedPlayerIds: prev.selectedPlayerIds,
      }));
      
      // Load template attachments
      try {
        const response = await apiRequest("GET", `/api/attachments/template/${templateId}`);
        if (response.ok) {
          const data = await response.json();
          
          if (data.success && data.attachments && data.attachments.length > 0) {
            const templateAttachments: AttachmentFile[] = data.attachments.map((attachment: any) => ({
              id: `template-${attachment.id}`,
              name: attachment.file_name,
              size: attachment.file_size,
              type: attachment.file_type,
              progress: 100,
              status: 'completed' as const,
              attachment_id: attachment.id,
              file_name: attachment.file_name,
              file_size: attachment.file_size,
              file_type: attachment.file_type,
              upload_date: attachment.upload_date,
              isExisting: true,
              serverId: attachment.id.toString()
            }));
            
            setAttachments(prevAttachments => {
              const nonTemplateAttachments = prevAttachments.filter(att => !att.id.startsWith('template-'));
              return [...nonTemplateAttachments, ...templateAttachments];
            });
            
            toast({
              title: "Template loaded",
              description: `Template "${template.name}" loaded with ${templateAttachments.length} attachment${templateAttachments.length !== 1 ? 's' : ''}`,
            });
          }
        }
      } catch (error) {
        console.error('Error loading template attachments:', error);
      }
    }
    
    // Reset the flag after a short delay to allow for template application
    setTimeout(() => {
      isApplyingTemplate.current = false;
    }, 500);
  };

  const insertVariable = (variable: keyof EmailVariables) => {
    if (focusedField === 'subject') {
      setFormData(prev => ({
        ...prev,
        subject: prev.subject + `{{${variable}}}`,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        content: prev.content + `{{${variable}}}`,
      }));
    }
  };

  const generateSignatureText = (sig: Signature): string => {
    const lines: string[] = [];
    lines.push("\n\n---");
    if (sig.name) lines.push(sig.name);
    if (sig.title) lines.push(sig.title);
    if (sig.organization) lines.push(sig.organization);
    if (sig.department) lines.push(sig.department);
    if (sig.email) lines.push(sig.email);
    if (sig.phone) lines.push(sig.phone);
    sig.other.filter(field => field.trim()).forEach(field => {
      lines.push(field);
    });
    return lines.join('\n');
  };

  const addSignatureToContent = () => {
    const signatureText = generateSignatureText(signature);
    setFormData(prev => ({
      ...prev,
      content: prev.content + signatureText,
    }));
    
    toast({
      title: "Signature added",
      description: "Your email signature has been added to the message.",
    });
  };

  const handleSignatureUpdate = (updatedSignature: Signature) => {
    setSignature(updatedSignature);
    queryClient.invalidateQueries({ queryKey: ["/api/preferences/"] });
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }
    
    if (!formData.content.trim()) {
      newErrors.content = "Message content is required";
    }
    
    if (playersWithEmails.length === 0) {
      newErrors.recipients = "No players with email addresses found";
    }

    const hasUploadingAttachments = attachments.some(att => att.status === 'uploading');
    if (hasUploadingAttachments) {
      newErrors.attachments = "Please wait for all attachments to finish uploading";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Debug logging for attachments (matching ComposeEmail implementation)
    console.log('=== EMAIL SENDING DEBUG (BulkEmailModal) ===');
    console.log('Current attachments state:', attachments);
    console.log('Attachments being sent:', attachments.map(att => ({
      id: att.id,
      name: att.name,
      status: att.status,
      attachment_id: att.attachment_id,
      serverId: att.serverId,
      isExisting: att.isExisting
    })));
    
    const emailData: SendEmailData = {
      to: formData.emailTags || [],
      templateId: formData.templateId,
      subject: formData.subject,
      content: formData.content,
      scheduledAt: formData.scheduledAt,
      trackOpens: formData.trackOpens,
      trackClicks: formData.trackClicks,
      recipientId: selectedPlayerForPreview?.id,
      emailTags: formData.emailTags,
      selectedPlayerData: selectedPlayerForPreview,
      attachments: attachments,
    };
    
    console.log('Email data being sent to backend:', emailData);
    console.log('Recipients count:', formData.emailTags?.length);
    console.log('=== END EMAIL SENDING DEBUG ===');
    
    // Use WarningManager to handle all warnings in the correct order
    if (warningManagerRef.current) {
      warningManagerRef.current.processWarnings(emailData);
    } else {
      // Fallback to direct send if WarningManager is not available
      sendEmailMutation.mutate(emailData);
    }
  };



  // No longer needed - warning handling is managed by WarningManager



  // MissingVariableWarningModal is now handled by WarningManager



  const handleAiTailor = async () => {
    if (!aiPrompt.trim()) {
      toast({
        title: "Prompt required",
        description: "Please enter a prompt for AI to tailor your email.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "🚀 Coming Soon",
      description: "AI email tailoring will be available in the next update!",
    });
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => {
        // Prevent closing if signature modal is open
        if (!open && showEditSignature) {
          return;
        }
        onClose();
      }}>
        <DialogContent className="sm:max-w-6xl max-h-[90vh] flex flex-col overflow-y-auto">
          <DialogHeader className="flex-shrink-0 pb-4">
            <DialogTitle className="flex items-center space-x-2">
              <i className="fas fa-envelope-open-text text-blue-600"></i>
              <span>{title}</span>
              <Badge variant="secondary" className="ml-2">
                {playersWithEmails.length} recipient{playersWithEmails.length !== 1 ? 's' : ''}
              </Badge>
            </DialogTitle>
            {subtitle && (
              <p className="text-sm text-gray-600">{subtitle}</p>
            )}
          </DialogHeader>

          <div className="flex gap-6 flex-1 overflow-hidden">
            {/* Left side - Email composition */}
            <div className="flex-1 flex flex-col min-w-0">
              <form onSubmit={handleSubmit} className="flex flex-col h-full">
                <div className="space-y-3 flex-1 overflow-y-auto pr-2">
                  {/* Top row - Recipients and Subject */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {/* Recipients */}
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-gray-700">Recipients ({playersWithEmails.length})</Label>
                      <div className="h-14 overflow-y-auto p-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 shadow-sm">
                        <div className="space-y-1">
                          {playersWithEmails.slice(0, 3).map((player) => (
                            <div key={player.id} className="flex items-center space-x-2 text-xs">
                              <div className="w-3 h-3 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                                <i className="fas fa-user text-white text-xs"></i>
                              </div>
                              <span className="font-medium text-gray-900 truncate text-xs">{player.name}</span>
                              <span className="text-gray-600 truncate text-xs">{player.email}</span>
                            </div>
                          ))}
                          {playersWithEmails.length > 3 && (
                            <div className="text-xs text-gray-500 text-center pt-0.5">
                              <i className="fas fa-ellipsis-h mr-1"></i>
                              and {playersWithEmails.length - 3} more
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Subject */}
                    <div className="space-y-1.5">
                      <Label htmlFor="subject" className="text-sm font-medium text-gray-700">Subject *</Label>
                      <Input
                        id="subject"
                        placeholder="Enter email subject..."
                        value={formData.subject}
                        onChange={(e) => handleInputChange("subject", e.target.value)}
                        onFocus={() => setFocusedField('subject')}
                        className={`${errors.subject ? "border-red-500" : "border-gray-300 focus:border-blue-500"} h-10 shadow-sm`}
                      />
                      {errors.subject && <p className="text-red-500 text-xs flex items-center mt-1"><i className="fas fa-exclamation-circle mr-1"></i>{errors.subject}</p>}
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-1.5 flex-1">
                    <Label htmlFor="content" className="text-sm font-medium text-gray-700">Message *</Label>
                    <div className="border border-gray-300 rounded-lg h-64 lg:h-80 overflow-hidden shadow-sm">
                      <div className="h-full">
                        <RichTextEditor
                          content={formData.content}
                          onChange={(value) => handleInputChange("content", value)}
                          placeholder="Write your message here..."
                          selectedPlayer={selectedPlayerForPreview?.email ? selectedPlayerForPreview as any : null}
                          variables={selectedPlayerForPreview ? {
                            PlayerName: selectedPlayerForPreview.name,
                            PlayerFirstName: splitPlayerName(selectedPlayerForPreview.name || '').firstName,
                            PlayerLastName: splitPlayerName(selectedPlayerForPreview.name || '').lastName,
                            PlayerPosition: (selectedPlayerForPreview.position && Array.isArray(selectedPlayerForPreview.position)) ? selectedPlayerForPreview.position.join(', ') : '',
                            PlayerClub: selectedPlayerForPreview.current_club || '',
                            PlayerLevel: selectedPlayerForPreview.playing_level || '',
                          } : undefined}
                          style={{ height: '100%', overflow: 'auto' }}
                        />
                      </div>
                    </div>
                    {errors.content && <p className="text-red-500 text-xs flex items-center mt-1"><i className="fas fa-exclamation-circle mr-1"></i>{errors.content}</p>}
                  </div>

                  {/* Bottom row - Attachments and AI */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {/* Attachments */}
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-gray-700">
                        <i className="fas fa-paperclip mr-1.5 text-gray-600"></i>
                        Attachments
                      </Label>
                      <div className="border border-gray-300 rounded-lg p-2.5 h-[120px] overflow-y-auto bg-gray-50/50 shadow-sm">
                        <AttachmentUpload
                          attachments={attachments}
                          onAttachmentsChange={(newAttachments) => {
                            setAttachments(newAttachments);
                            setFormData(prev => ({ ...prev, attachments: newAttachments }));
                          }}
                          maxFileSize={25 * 1024 * 1024}
                          maxTotalSize={100 * 1024 * 1024}
                          maxFiles={10}
                          disabled={sendEmailMutation.isPending}
                          showExisting={false}
                        />
                      </div>
                      {errors.attachments && <p className="text-red-500 text-xs flex items-center mt-1"><i className="fas fa-exclamation-circle mr-1"></i>{errors.attachments}</p>}
                    </div>

                    {/* AI Email Customization */}
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-gray-700">
                        <i className="fas fa-robot mr-1.5 text-blue-600"></i>
                        AI Customization
                        <Badge variant="outline" className="ml-2 text-xs border-blue-300 text-blue-700 bg-blue-50">
                          Coming Soon
                        </Badge>
                      </Label>
                      <div className="border-2 border-dashed border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-2.5 h-[120px] flex flex-col shadow-sm">
                        <Textarea
                          placeholder="E.g., 'Make this more formal and professional'..."
                          className="resize-none bg-white flex-1 text-sm border-gray-200 shadow-sm"
                          value={aiPrompt}
                          onChange={(e) => setAiPrompt(e.target.value)}
                          disabled
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mt-2 border-blue-300 text-blue-700 hover:bg-blue-100 text-xs h-7"
                          onClick={handleAiTailor}
                          disabled
                        >
                          <i className="fas fa-magic mr-1.5"></i>
                          Customize with AI
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions - Fixed at bottom */}
                <div className="flex justify-between items-center pt-3 mt-3 border-t border-gray-200 bg-white flex-shrink-0">
                  <div className="flex space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addSignatureToContent}
                      size="sm"
                      className="text-gray-700 border-gray-300 hover:bg-gray-50"
                    >
                      <i className="fas fa-signature mr-1.5"></i>
                      Add Signature
                    </Button>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onClose}
                      className="text-gray-700 border-gray-300 hover:bg-gray-50"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={sendEmailMutation.isPending || attachments.some(att => att.status === 'uploading') || playersWithEmails.length === 0}
                      className="bg-blue-600 hover:bg-blue-700 shadow-sm"
                      title={playersWithEmails.length === 0 ? "No players with email addresses to send to" : ""}
                    >
                      {sendEmailMutation.isPending ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                          Sending...
                        </>
                      ) : playersWithEmails.length === 0 ? (
                        <>
                          <i className="fas fa-exclamation-triangle mr-2"></i>
                          No Email Recipients
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane mr-2"></i>
                          Send to {playersWithEmails.length} Recipients
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            </div>

            {/* Right side - Tools & Options - Increased width with scrollable content */}
            <div className="w-96 flex flex-col space-y-4 overflow-y-auto max-h-full">
              {/* Templates */}
              <Card className="flex-shrink-0">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center">
                    <i className="fas fa-file-alt mr-2 text-purple-600"></i>
                    Templates
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Select
                    value={formData.templateId?.toString() || ""}
                    onValueChange={(value) => handleTemplateSelect(value)}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select a template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates?.map((template) => (
                        <SelectItem key={template.id} value={template.id.toString()}>
                          <div className="flex items-center space-x-2">
                            <i className="fas fa-file-alt text-xs"></i>
                            <span>{template.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>

              {/* Variables - Fixed height with scrollable content */}
              <Card className="h-[33rem] flex-shrink-0">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center">
                    <i className="fas fa-code mr-2 text-green-600"></i>
                    Variables
                    {selectedPlayerForPreview && (
                      <Badge variant="secondary" className="ml-2 text-xs">
                        {selectedPlayerForPreview.name}
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="h-[25rem] space-y-2 flex flex-col">
                  {/* Preview Player Selector */}
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Preview From:</Label>
                    <Select
                      value={selectedPlayerForPreview?.id.toString() || ""}
                      onValueChange={(value) => {
                        const player = playersWithEmails.find(p => p.id.toString() === value);
                        setSelectedPlayerForPreview(player || null);
                      }}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Select player" />
                      </SelectTrigger>
                      <SelectContent>
                        {playersWithEmails.map((player) => (
                          <SelectItem key={player.id} value={player.id.toString()}>
                            <div className="flex items-center space-x-2">
                              <i className="fas fa-user text-xs"></i>
                              <span>{player.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1 flex-1 overflow-y-auto min-h-0">
                    {selectedPlayerForPreview && ([
                      { 
                        label: "Player Name (Full)", 
                        variable: "PlayerName",
                        value: selectedPlayerForPreview.name || "Player Name",
                        icon: "fas fa-user"
                      },
                      { 
                        label: "First Name", 
                        variable: "PlayerFirstName",
                        value: splitPlayerName(selectedPlayerForPreview.name || '').firstName || "First Name",
                        icon: "fas fa-user"
                      },
                      { 
                        label: "Last Name", 
                        variable: "PlayerLastName", 
                        value: splitPlayerName(selectedPlayerForPreview.name || '').lastName || "Last Name",
                        icon: "fas fa-user"
                      },
                      { 
                        label: "Position", 
                        variable: "PlayerPosition",
                        value: (selectedPlayerForPreview.position && Array.isArray(selectedPlayerForPreview.position)) ? selectedPlayerForPreview.position.join(', ') : "Position",
                        icon: "fas fa-running"
                      },
                      { 
                        label: "Current Club", 
                        variable: "PlayerClub",
                        value: selectedPlayerForPreview.current_club || "Current Club",
                        icon: "fas fa-shield-alt"
                      },
                      { 
                        label: "Playing Level", 
                        variable: "PlayerLevel",
                        value: selectedPlayerForPreview.playing_level || "Playing Level",
                        icon: "fas fa-trophy"
                      },
                    ] as const).map(({ label, variable, value, icon }) => (
                      <div key={variable} className="flex justify-between items-center p-1.5 hover:bg-gray-50 rounded transition-colors">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-1 text-xs text-gray-600">
                            <i className={`${icon} w-3`}></i>
                            <span>{label}</span>
                          </div>
                          <div className="text-xs text-green-700 font-medium truncate">{value}</div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => insertVariable(variable)}
                          className="h-7 px-2 hover:bg-blue-100"
                        >
                          <code className="bg-gray-200 px-1 py-0.5 rounded text-xs">
                            {`{{${variable}}}`}
                          </code>
                        </Button>
                      </div>
                    ))}
                  </div>

                </CardContent>
              </Card>
            </div>
          </div>


        </DialogContent>
      </Dialog>



      {/* Signature Modal */}
      <SignatureModal
        isOpen={showEditSignature}
        onClose={() => setShowEditSignature(false)}
        signature={signature}
        onSignatureUpdate={handleSignatureUpdate}
      />

      {/* Centralized Warning Management */}
      <WarningManager
        ref={warningManagerRef}
        onCheckGraduationWarnings={checkGraduationWarnings}
        onCheckDuplicateEmails={checkDuplicateEmails}
        onValidateMissingVariables={validateVariablesForPlayers}
        onSendEmail={(emailData) => sendEmailMutation.mutate(emailData)}
        players={playersWithEmails}
      />
    </>
  );
} 